import { useState, useEffect } from 'react';

// Material UI
import { Page } from 'components/ui/layout';
import { Typography, Grid } from '@mui/material';

// Components
import { Section, Header, Content } from 'components/ui/forms';
import QuestionCreator from 'components/forms/QuestionCreator';
import QuestionEditor from 'components/forms/QuestionEditor';
import FullScreenModal from 'components/popups/FullScreenModal';
import { LoadingPage, ErrorPage } from 'components/ui/states';

// Components - Local
import RegistrationModePicker from './RegistrationModePicker';
import RegistrationAttendeeSettings from './RegistrationAttendeeSettings';
import RegistrationQuestions from './RegistrationQuestions';

// API
import { getRegistrationPageData, updateRegistrationQuestionsSort } from './RegistrationAPI';

// Utility
import { useNotification } from 'context/notification';
import { useEvent } from 'context/event';

export default function CheckoutForm(props) {
    const eventId = props.match.params.id;

    const event = useEvent();

    const [modal, setModal] = useState({
        open: false,
        questionId: null,
    });

    const [questions, setQuestions] = useState({
        status: 'loading', // loading, error, success
        original: [],
        display: [],
        sort: false,
        refresh: 0,
    });

    const [status, setStatus] = useState({
        mode: null, // 0 (disabled), 1 (order), 2 (attendee)
        firstName: false, // require attendee first names (attendee-mode only)
        lastName: false, // require attendee last names (attendee-mode only)
        email: false, // require attendee emails (attendee-mode only)
        loading: false,
    });

    const { notify } = useNotification();

    useEffect(() => {
        async function init() {
            try {
                const { registration, questions: questionList } =
                    await getRegistrationPageData(eventId);

                setStatus((s) => ({
                    ...s,
                    mode: registration.mode,
                    firstName: Boolean(registration.firstName),
                    lastName: Boolean(registration.lastName),
                    email: Boolean(registration.email),
                }));

                setQuestions((q) => ({
                    ...q,
                    status: 'success',
                    original: questionList.map((q, i) => ({
                        ...q,
                        order: i + 1,
                        highlight: false,
                    })),
                    display: questionList.map((q, i) => ({
                        ...q,
                        order: i + 1,
                        highlight: false,
                    })),
                }));
            } catch (error) {
                setQuestions((q) => ({ ...q, status: 'error' }));
            }
        }
        init();
    }, [eventId, questions.refresh]);

    const handleRegistrationModeUpdate = (mode) => {
        setStatus((s) => ({ ...s, mode: mode }));
    };

    const handleRequiredInfoUpdate = (firstName, lastName, email) => {
        setStatus((s) => ({
            ...s,
            firstName: firstName,
            lastName: lastName,
            email: email,
        }));
    };

    const createQuestion = () => setModal({ open: true, questionId: null });
    const editQuestion = (id) => setModal({ open: true, questionId: id });

    const closeQuestion = () => setModal({ open: false, questionId: null });

    const handleQuestionUpdate = () => {
        closeQuestion();
        setQuestions((q) => ({ ...q, refresh: q.refresh + 1 }));
    };

    function handleSort(id, direction) {
        const display = JSON.parse(JSON.stringify(questions.display)); // Deep clone

        const targetIndex = display.findIndex((q) => q.q_id === id); // Get index of question to move
        const destinationIndex = direction === 'up' ? targetIndex - 1 : targetIndex + 1; // Get index of question to swap with

        const targetQuestion = display[targetIndex];
        const destinationQuestion = display[destinationIndex];

        const questionList = JSON.parse(JSON.stringify(display)); // Deep clone

        questionList[targetIndex] = destinationQuestion;
        questionList[destinationIndex] = targetQuestion;

        // Highlight the question that was just moved
        for (let i = 0; i < questionList.length; i++) {
            if (i === destinationIndex) {
                questionList[i].highlight = true;
            } else {
                questionList[i].highlight = false;
            }
        }

        setQuestions((q) => ({ ...q, display: questionList }));
    }

    function handleSortSubmit() {
        updateRegistrationQuestionsSort(
            eventId,
            questions.display.map((q) => q.q_id),
        )
            .then(() => {
                setQuestions((q) => ({ ...q, sort: false, refresh: q.refresh + 1 }));
                notify.success('Questions have been re-ordered');
            })
            .catch(() => {
                notify.error('Unable to change order');
            });
    }

    function handleSortStart() {
        setQuestions((q) => ({ ...q, sort: !q.sort }));
    }

    function handleSortCancel() {
        setQuestions((q) => ({ ...q, display: q.original, sort: !q.sort }));
    }

    if (questions.status === 'loading') {
        return <LoadingPage />;
    }

    if (questions.status === 'error') {
        return <ErrorPage message="Unable to load checkout questions" />;
    }

    return (
        <Page>
            <Grid container spacing={4}>
                <Grid item xs={12}>
                    <Section>
                        <Header>Ask Questions at Checkout</Header>
                        <Typography>
                            Collect additional information from your customers by asking them
                            questions during checkout.
                        </Typography>
                        <Content>
                            <RegistrationModePicker
                                eventId={eventId}
                                enableAttendeeMode={event.seats_flag === 0}
                                mode={status.mode}
                                onUpdate={handleRegistrationModeUpdate}
                            />
                        </Content>
                    </Section>
                </Grid>
                {status.mode === 2 && (
                    <Grid item xs={12}>
                        <Section>
                            <Header>Attendee Configuration</Header>
                            <Typography>Configure which of your attendees will be asked questions.</Typography>
                            <Content>
                                <RegistrationAttendeeSettings
                                    eventId={eventId}
                                    loading={status.loading}
                                    firstName={status.firstName}
                                    lastName={status.lastName}
                                    email={status.email}
                                    onUpdate={handleRequiredInfoUpdate}
                                />
                            </Content>
                        </Section>
                    </Grid>
                )}
                <Grid item xs={12}>
                    <Section>
                        <Header>Checkout Questions</Header>
                        <Typography>
                            Add the questions you want to ask customers during checkout. Keep them
                            short and relevant; longer checkout processes can lead to fewer
                            completed purchases.
                        </Typography>
                        <Content>
                            <RegistrationQuestions
                                mode={status.mode}
                                firstName={status.firstName}
                                lastName={status.lastName}
                                email={status.email}
                                questions={questions.display}
                                onNewQuestion={createQuestion}
                                onQuestionSelect={editQuestion}
                                onSort={handleSort}
                                enableSort={questions.sort}
                                onSortStart={handleSortStart}
                                onSortCancel={handleSortCancel}
                                onSortSubmit={handleSortSubmit}
                            />
                        </Content>
                    </Section>
                </Grid>
            </Grid>

            <FullScreenModal
                open={modal.open}
                onClose={closeQuestion}
                title={modal.questionId ? 'Edit Question' : 'Create Question'}
                cancelText="Cancel"
            >
                {modal.questionId ? (
                    <QuestionEditor
                        questionId={modal.questionId}
                        eventId={eventId}
                        onEdit={handleQuestionUpdate}
                        onCancel={closeQuestion}
                    />
                ) : (
                    <QuestionCreator
                        eventId={eventId}
                        onCreate={handleQuestionUpdate}
                        onCancel={closeQuestion}
                    />
                )}
            </FullScreenModal>
        </Page>
    );
}
